import { Modal, TextInput, Button, Stack, Group, Text, Loader, Alert } from '@mantine/core';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState, useEffect } from 'react';

const addJobSchema = z.object({
  url: z.string().url('Please enter a valid URL').min(1, 'URL is required'),
});

type AddJobFormData = z.infer<typeof addJobSchema>;

interface AddJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (url: string) => Promise<void>;
  isLoading?: boolean;
  error?: string;
  initialUrl?: string;
}

export default function AddJobModal({ isOpen, onClose, onSubmit, isLoading = false, error, initialUrl }: AddJobModalProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<AddJobFormData>({
    resolver: zodResolver(addJobSchema),
    defaultValues: {
      url: initialUrl || '',
    },
  });

  useEffect(() => {
    if (isOpen && initialUrl) {
      setValue('url', initialUrl);
    }
  }, [isOpen, initialUrl, setValue]);

  const onFormSubmit = async (data: AddJobFormData) => {
    try {
      await onSubmit(data.url);
      reset();
      onClose();
    } catch (err) {
      // Error is handled by parent component
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title="Add Job from URL"
      size="md"
      centered
      closeOnEscape={!isLoading}
      closeOnClickOutside={!isLoading}
    >
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Stack gap="md">
          {error && <Alert color="red">{error}</Alert>}

          <div>
            <Text size="sm" fw={500} mb={8}>
              Job Posting URL
            </Text>
            <TextInput
              placeholder="https://job-boards.greenhouse.io/example"
              {...register('url')}
              error={errors.url?.message}
              disabled={isLoading}
              aria-label="Job URL"
            />
            <Text size="xs" c="dimmed" mt={6}>
              Paste the URL of the job posting. We'll automatically extract the title, company, location, and description.
            </Text>
          </div>

          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" loading={isLoading} disabled={isLoading}>
              {isLoading ? 'Scraping...' : 'Add Job'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
